import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ExternalLink, CreditCard } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

interface ConnectOnboardingProps {
  userHasStripeAccount?: boolean;
  isFullyOnboarded?: boolean;
  canReceivePayments?: boolean;
  hasPhone?: boolean;
  onOnboardingComplete?: () => void;
}

export function ConnectOnboarding({ userHasStripeAccount, isFullyOnboarded, canReceivePayments, hasPhone, onOnboardingComplete }: ConnectOnboardingProps) {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Refetch Stripe status when user returns to page (after completing onboarding in another tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !isFullyOnboarded) {
        console.log("Page visible again, refetching Stripe status");
        queryClient.invalidateQueries({ queryKey: ['stripeConnect'] });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isFullyOnboarded, queryClient]);

  const handleStartOnboarding = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.functions.invoke('connect-onboarding-link');
      
      if (error) throw error;
      
      if (data?.url) {
        // Open Stripe onboarding in new tab
        window.open(data.url, '_blank');
        toast({
          title: "Onboarding startet",
          description: "Følg instruktionerne på Stripe. Når du er færdig, vend tilbage til denne side.",
        });
        
        // Poll for status updates every 5 seconds after opening onboarding
        const pollInterval = setInterval(() => {
          console.log("Polling Stripe status...");
          queryClient.invalidateQueries({ queryKey: ['stripeConnect'] });
        }, 5000);
        
        // Stop polling after 5 minutes
        setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
        
        onOnboardingComplete?.();
      } else {
        throw new Error('No onboarding URL received');
      }
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast({
        title: "Fejl",
        description: error instanceof Error ? error.message : 'Kunne ikke starte onboarding',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Only show success if fully onboarded AND can receive payments
  if (userHasStripeAccount && isFullyOnboarded && canReceivePayments) {
    return (
      <Card className="border-green-200 bg-green-50 dark:bg-green-950/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-600" />
            <CardTitle className="text-green-800 dark:text-green-200">
              Udbetalinger aktiveret ✓
            </CardTitle>
          </div>
          <CardDescription className="text-green-700 dark:text-green-300">
            Din konto er klar til at modtage betalinger og udbetalinger er aktiveret.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => window.open('https://connect.stripe.com/express_login', '_blank')}
            variant="outline"
            className="w-full"
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Se Stripe dashboard
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Åbn Stripe for at se dine udbetalinger og transaktioner.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show partial onboarding state
  if (userHasStripeAccount && !isFullyOnboarded) {
    return (
      <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-yellow-600" />
            <CardTitle className="text-yellow-800 dark:text-yellow-200">
              Onboarding under behandling
            </CardTitle>
          </div>
          <CardDescription className="text-yellow-700 dark:text-yellow-300">
            Din Stripe konto er oprettet, men venter på godkendelse. Dette kan tage nogle minutter.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleStartOnboarding}
            disabled={loading}
            variant="outline"
            className="w-full"
          >
            {loading ? "Starter..." : "Genoptag onboarding"}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Show warning if user doesn't have phone number
  if (!hasPhone) {
    return (
      <Card className="border-red-200 bg-red-50 dark:bg-red-950/10">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-red-600" />
            <CardTitle className="text-red-800 dark:text-red-200">
              Telefonnummer påkrævet
            </CardTitle>
          </div>
          <CardDescription className="text-red-700 dark:text-red-300">
            Du skal tilføje et telefonnummer til din profil før du kan aktivere udbetalinger.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => window.location.href = '/profile'}
            variant="outline"
            className="w-full"
          >
            Gå til profil
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 bg-orange-50 dark:bg-orange-950/10">
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-orange-600" />
          <CardTitle className="text-orange-800 dark:text-orange-200">
            Aktivér udbetalinger
          </CardTitle>
        </div>
        <CardDescription className="text-orange-700 dark:text-orange-300">
          For at modtage betaling fra gæster skal du først opsætte din udbetaling via Stripe.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button
          onClick={handleStartOnboarding}
          disabled={loading}
          className="w-full"
        >
          {loading ? (
            "Starter..."
          ) : (
            <>
              <ExternalLink className="mr-2 h-4 w-4" />
              Opsæt udbetalinger
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          Du vil blive omdirigeret til Stripe for at opsætte din konto sikkert.
        </p>
      </CardContent>
    </Card>
  );
}